import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, untracked } from '@angular/core';
import { map, Observable } from 'rxjs';
import { LangStore } from '../../core/lang.service';
import { AuthApi } from '../api/auth.api';
import { toAuthSession, toCodeRequest, toTokenPair } from '../mappers/auth.mapper';
import type { AuthSession, CodeRequest } from '../models/auth/user.model';
import type { ApiEnvelope } from './api-envelope';
import { apiPath, mapApiError, unwrapEnvelope } from './api-envelope';

/** Один источник строк: интерцепторам нужны те же пути, иначе исключения по 401 разъедутся с реальностью. */
export const AUTH_PATHS = {
  code: apiPath('api/users/client/auth/code'),
  verify: apiPath('api/users/client/auth/verify'),
  logout: apiPath('api/users/client/auth/logout'),
  telegram: apiPath('api/users/client/auth/telegram'),
  telegramLink: apiPath('api/users/client/auth/telegram/link'),
  /** Путь общий, НЕ под `/api/users/client` — см. docs/client-website-api-guide.md § 3.4. */
  refresh: apiPath('api/auth/refreshtoken'),
} as const;

@Injectable()
export class AuthHttpApi extends AuthApi {
  private readonly http = inject(HttpClient);
  private readonly lang = inject(LangStore);

  override requestCode(phone: string): Observable<CodeRequest> {
    // `language` — язык текста SMS: бэкенд ждёт верхний регистр ('RU' | 'UZ').
    return this.http
      .post<ApiEnvelope<unknown>>(AUTH_PATHS.code, {
        phone,
        language: untracked(this.lang.lang).toUpperCase(),
      })
      .pipe(unwrapEnvelope<unknown>(), map(toCodeRequest), mapApiError());
  }

  override confirmCode(phone: string, code: string): Observable<AuthSession> {
    // Код уходит ЧИСЛОМ: на строку сервер отвечает 400 JSON_PARSING_ERROR_CODE. `fullName` не шлём (Б-4).
    return this.http
      .post<ApiEnvelope<unknown>>(AUTH_PATHS.verify, { phone, code: Number(code) })
      .pipe(unwrapEnvelope<unknown>(), map(toAuthSession), mapApiError());
  }

  override telegramLogin(initData: string): Observable<AuthSession | null> {
    return this.http.post<ApiEnvelope<unknown>>(AUTH_PATHS.telegram, { initData }).pipe(
      unwrapEnvelope<unknown>(),
      // unwrapEnvelope превращает `response: null` в undefined, а маппер на нём бросил бы «сетевую» ошибку.
      map((value) => (value == null ? null : toAuthSession(value))),
      mapApiError(),
    );
  }

  override linkTelegram(initData: string): Observable<void> {
    return this.http.post<unknown>(AUTH_PATHS.telegramLink, { initData }).pipe(
      map((): void => undefined),
      mapApiError(),
    );
  }

  override refresh(refreshToken: string): Observable<{ token: string; refreshToken: string }> {
    return this.http
      .post<ApiEnvelope<unknown>>(AUTH_PATHS.refresh, { refreshToken })
      .pipe(unwrapEnvelope<unknown>(), map(toTokenPair), mapApiError());
  }

  override logout(unlinkTelegram: boolean): Observable<void> {
    // false и «не передан» для бэкенда одно и то же: вне Telegram запрос остаётся прежним, без параметра.
    const params = unlinkTelegram ? new HttpParams().set('unlinkTelegram', true) : undefined;
    // Тело запроса и ответа пустые: успех — сам факт 200, конверт разбирать нечего.
    return this.http.post<unknown>(AUTH_PATHS.logout, null, { params }).pipe(
      map((): void => undefined),
      mapApiError(),
    );
  }
}
